declare global {
  interface Window {
    __tzRefreshFetchPatched?: boolean;
  }
}

const REFRESH_TOKEN_PATH_SEGMENT = "/api/v1/refresh_token";

type FetchInput = Parameters<typeof window.fetch>[0];
type FetchInit = Parameters<typeof window.fetch>[1];

type RefreshRequestEntry = {
  fetchPromise: Promise<Response>;
  clonePromise: Promise<Response>;
};

function resolveUrl(input: FetchInput): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Request) {
    return input.url;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return String(input);
}

function resolveMethod(input: FetchInput, init?: FetchInit): string {
  if (init?.method) {
    return init.method.toUpperCase();
  }

  if (input instanceof Request && input.method) {
    return input.method.toUpperCase();
  }

  return "GET";
}

const shouldDedup = (method: string, url: string) => {
  return method === "GET" && url.includes(REFRESH_TOKEN_PATH_SEGMENT);
};

if (typeof window !== "undefined" && !window.__tzRefreshFetchPatched) {
  const originalFetch = window.fetch.bind(window);
  const inflightRequests = new Map<string, RefreshRequestEntry>();

  window.fetch = (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const method = resolveMethod(input, init);
    const url = resolveUrl(input);

    if (!shouldDedup(method, url)) {
      return originalFetch(input as RequestInfo | URL, init);
    }

    const key = `${method}:${url}`;
    const existingEntry = inflightRequests.get(key);

    if (existingEntry) {
      return existingEntry.clonePromise.then((baseClone) => baseClone.clone());
    }

    const originalPromise = originalFetch(input as RequestInfo | URL, init);

    const entry: RefreshRequestEntry = {
      fetchPromise: originalPromise
        .then((response) => {
          inflightRequests.delete(key);
          return response;
        })
        .catch((error) => {
          inflightRequests.delete(key);
          throw error;
        }),
      clonePromise: originalPromise.then((response) => response.clone()),
    };

    inflightRequests.set(key, entry);

    return entry.fetchPromise;
  };

  window.__tzRefreshFetchPatched = true;
}

export {};
